<template>
    <Modal close-esc-event="keydown" :close-button="false" :modal-class="`m-auto border-0 max-w-6xl transition-width duration-500`" mask-class="grid">
        <template #toggle="scope">
            <slot v-bind="scope" />
        </template>

        <template #header="scope">
            <CloseModalButton @close="scope.close"></CloseModalButton>
        </template>

        <template #body="scope">
            <div class="bg-white w-full relative rounded-lg grid py-6 px-10 m-auto space-y-4 max-w-128">
                <MultiStepFormHeaderIcon :icon-source="IconSource.Food" :is-final-step="false"></MultiStepFormHeaderIcon>
                <h2 class="text-xl font-semibold text-center">{{ $t('views.feed.reconciliations.deleteModal.header') }}</h2>
                <p class="text-center">
                    <span>{{ $t('views.feed.reconciliations.deleteModal.message') }}</span><br />
                    <span class="font-bold text-primary-700">{{ formattedPeriod }}, {{ reconciliation.location.name }}?</span>
                </p>
                <div class="flex space-x-4">
                    <CustomButton :disabled="disableButtons" color-preset="secondary" @click="scope.close">
                        {{ $t('views.feed.reconciliations.deleteModal.cancelButton') }}
                    </CustomButton>
                    <CustomButton :loading="disableButtons" color-preset="warning" @click="onDeleteClicked(scope.close)">
                        {{ $t('views.feed.reconciliations.deleteModal.confirmButton') }}
                    </CustomButton>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import { Modal } from '@makeabledk/vue-ui/components/modal';
import CustomButton from '@/components/ui/CustomButton.vue';
import { FeedReconciliation, IconSource } from '@/types';
import MultiStepFormHeaderIcon from '@/components/common/MultiStepFormHeaderIcon.vue';
import { useToast } from 'vue-toastification';
import { ActionType } from '@/plugins/store/actions';
import { store } from '@/plugins/store';
import CloseModalButton from '@/components/common/CloseModalButton.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import { queryKeys } from '@/plugins/store/actions/queries/feedReconciliations';

export default defineComponent({
    components: { Modal, MultiStepFormHeaderIcon, CustomButton, CloseModalButton },
    props: {
        reconciliation: {
            type: Object as () => FeedReconciliation,
            required: true,
        },
    },
    emits: ['deleted'],
    setup() {
        return { queryClient: useQueryClient() };
    },
    data() {
        return { disableButtons: false, IconSource, toast: useToast() };
    },
    computed: {
        formattedPeriod(): string {
            const from = i18n.global.d(new Date(this.reconciliation.from_status_date), 'short');
            const to = i18n.global.d(new Date(this.reconciliation.to_status_date), 'short');
            return `${from} – ${to}`;
        },
    },
    methods: {
        async onDeleteClicked(close: CallableFunction) {
            try {
                this.disableButtons = true;
                await store.dispatch(ActionType.DeleteFeedReconciliation, { id: this.reconciliation.id });
                this.queryClient.invalidateQueries({ queryKey: queryKeys.getFeedReconciliationsRoot });
                this.$emit('deleted');
                this.disableButtons = false;
                close();
            } catch (err) {
                this.disableButtons = false;
                this.toast.error(this.$t('global.errors.unknown'));
            }
        },
    },
});
</script>
